import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import config from "../config/config";

const API_URL = config.apiUrl;

export const api = createApi({
  reducerPath: "melonaApi",
  tagTypes: ["Dashboard", "Orders", "Customers", "Payments", "Tasks", "Employees", "Stock", "Gallery", "Cashflows", "OrderAllocations", "Todos", "ContactSubmissions"],
  baseQuery: fetchBaseQuery({
    baseUrl: API_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.token;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    login: builder.mutation({
      query: (credentials) => ({
        url: "/auth/login",
        method: "POST",
        body: credentials,
      }),
    }),
    getDashboard: builder.query({
      query: () => "/dashboard",
      providesTags: ["Dashboard", "Orders", "Payments", "Stock", "Tasks", "Employees", "Customers"],
    }),
    getOrders: builder.query({
      query: () => "/orders",
      providesTags: ["Orders"],
    }),
    createOrder: builder.mutation({
      query: (body) => ({
        url: "/orders",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Orders", "Dashboard"],
    }),
    updateOrder: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/orders/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Orders", "Dashboard"],
    }),
    deleteOrder: builder.mutation({
      query: (id) => ({
        url: `/orders/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Orders", "Dashboard"],
    }),
    getCustomers: builder.query({
      query: () => "/customers",
      providesTags: ["Customers"],
    }),
    createCustomer: builder.mutation({
      query: (body) => ({
        url: "/customers",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Customers", "Orders", "Dashboard"],
    }),
    updateCustomer: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/customers/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Customers", "Orders", "Dashboard"],
    }),
    deleteCustomer: builder.mutation({
      query: (id) => ({
        url: `/customers/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Customers", "Orders", "Dashboard"],
    }),
    getPayments: builder.query({
      query: () => "/payments",
      providesTags: ["Payments"],
    }),
    getOrderPayments: builder.query({
      query: (orderId) => `/payments?order_id=${orderId}`,
      providesTags: (_r, _e, orderId) => [{ type: "Payments", id: `order-${orderId}` }],
    }),
    createPayment: builder.mutation({
      query: (body) => ({
        url: "/payments",
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, body) => [
        "Payments",
        "Orders",
        "Dashboard",
        { type: "Payments", id: `order-${body?.order_id}` },
      ],
    }),
    updatePayment: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/payments/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { order_id }) => [
        "Payments",
        "Orders",
        "Dashboard",
        { type: "Payments", id: `order-${order_id}` },
      ],
    }),
    deletePayment: builder.mutation({
      query: (id) => ({
        url: `/payments/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Payments", "Orders", "Dashboard"],
    }),
    getTasks: builder.query({
      query: () => "/tasks",
      providesTags: ["Tasks"],
    }),
    createTask: builder.mutation({
      query: (body) => ({
        url: "/tasks",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Tasks", "Dashboard"],
    }),
    updateTask: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/tasks/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Tasks", "Dashboard"],
    }),
    deleteTask: builder.mutation({
      query: (id) => ({
        url: `/tasks/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Tasks", "Dashboard"],
    }),
    getEmployees: builder.query({
      query: () => "/employees",
      providesTags: ["Employees"],
    }),
    getGalleryItems: builder.query({
      query: () => "/gallery",
      providesTags: ["Gallery"],
    }),
    createGalleryItem: builder.mutation({
      query: (body) => ({
        url: "/gallery",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Gallery"],
    }),
    updateGalleryItem: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/gallery/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Gallery"],
    }),
    deleteGalleryItem: builder.mutation({
      query: (id) => ({
        url: `/gallery/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Gallery"],
    }),
    createEmployee: builder.mutation({
      query: (body) => ({
        url: "/employees",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Employees", "Dashboard"],
    }),
    updateEmployee: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/employees/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Employees", "Dashboard"],
    }),
    deleteEmployee: builder.mutation({
      query: (id) => ({
        url: `/employees/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Employees", "Dashboard"],
    }),
    getStockItems: builder.query({
      query: () => "/stock/items",
      providesTags: ["Stock"],
    }),
    getStockMovements: builder.query({
      query: () => "/stock/movements",
      providesTags: ["Stock"],
    }),
    createStockMovement: builder.mutation({
      query: (body) => ({
        url: "/stock/movements",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Stock", "Dashboard"],
    }),
    createStockItem: builder.mutation({
      query: (body) => ({
        url: "/stock/items",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Stock", "Dashboard"],
    }),
    updateStockItem: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/stock/items/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Stock", "Dashboard"],
    }),
    deleteStockItem: builder.mutation({
      query: (id) => ({
        url: `/stock/items/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Stock", "Dashboard"],
    }),
    updateStockMovement: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/stock/movements/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Stock", "Dashboard"],
    }),
    deleteStockMovement: builder.mutation({
      query: (id) => ({
        url: `/stock/movements/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Stock", "Dashboard"],
    }),
    getCashflows: builder.query({
      query: (category) => category ? `/cashflows?category=${category}` : "/cashflows",
      providesTags: ["Cashflows"],
    }),
    createCashflow: builder.mutation({
      query: (body) => ({
        url: "/cashflows",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Cashflows", "Dashboard"],
    }),
    updateCashflow: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/cashflows/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Cashflows", "Dashboard"],
    }),
    deleteCashflow: builder.mutation({
      query: (id) => ({
        url: `/cashflows/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Cashflows", "Dashboard"],
    }),
    // ── Order stock allocations ──────────────────────────────
    getOrderAllocations: builder.query({
      query: (orderId) => `/orders/${orderId}/stock-allocations`,
      providesTags: (_r, _e, orderId) => [{ type: "OrderAllocations", id: orderId }],
    }),
    allocateStockForOrder: builder.mutation({
      query: ({ orderId, ...body }) => ({
        url: `/orders/${orderId}/stock-allocations`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { orderId }) => [
        { type: "OrderAllocations", id: orderId },
        "Stock",
        "Dashboard",
      ],
    }),
    // ── Todos ────────────────────────────────────────────────
    getTodos: builder.query({
      query: () => "/todos",
      providesTags: ["Todos"],
    }),
    createTodo: builder.mutation({
      query: (body) => ({ url: "/todos", method: "POST", body }),
      invalidatesTags: ["Todos"],
    }),
    updateTodo: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/todos/${id}`, method: "PATCH", body }),
      invalidatesTags: ["Todos"],
    }),
    deleteTodo: builder.mutation({
      query: (id) => ({ url: `/todos/${id}`, method: "DELETE" }),
      invalidatesTags: ["Todos"],
    }),
    // ── Contact Submissions (public + admin) ────────────────
    submitContactForm: builder.mutation({
      query: (body) => ({ url: "/contact", method: "POST", body }),
    }),
    getContactSubmissions: builder.query({
      query: () => "/contact/admin",
      providesTags: ["ContactSubmissions"],
    }),
    getContactStats: builder.query({
      query: () => "/contact/admin/stats",
      providesTags: ["ContactSubmissions"],
    }),
    updateContactSubmission: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/contact/admin/${id}`, method: "PUT", body }),
      invalidatesTags: ["ContactSubmissions"],
    }),
    deleteContactSubmission: builder.mutation({
      query: (id) => ({ url: `/contact/admin/${id}`, method: "DELETE" }),
      invalidatesTags: ["ContactSubmissions"],
    }),
  }),
});

export const {
  useAllocateStockForOrderMutation,
  useCreateCashflowMutation,
  useCreateTodoMutation,
  useCreateEmployeeMutation,
  useCreateCustomerMutation,
  useCreateGalleryItemMutation,
  useCreateOrderMutation,
  useCreatePaymentMutation,
  useCreateStockItemMutation,
  useCreateStockMovementMutation,
  useCreateTaskMutation,
  useDeleteCustomerMutation,
  useDeleteCashflowMutation,
  useDeleteContactSubmissionMutation,
  useDeleteTodoMutation,
  useDeleteEmployeeMutation,
  useDeleteGalleryItemMutation,
  useDeleteOrderMutation,
  useDeletePaymentMutation,
  useDeleteStockItemMutation,
  useDeleteStockMovementMutation,
  useDeleteTaskMutation,
  useGetContactStatsQuery,
  useGetContactSubmissionsQuery,
  useGetCustomersQuery,
  useGetCashflowsQuery,
  useGetOrderAllocationsQuery,
  useGetOrderPaymentsQuery,
  useGetTodosQuery,
  useGetDashboardQuery,
  useGetEmployeesQuery,
  useGetGalleryItemsQuery,
  useGetOrdersQuery,
  useGetPaymentsQuery,
  useGetStockItemsQuery,
  useGetStockMovementsQuery,
  useGetTasksQuery,
  useLoginMutation,
  useSubmitContactFormMutation,
  useUpdateContactSubmissionMutation,
  useUpdateCustomerMutation,
  useUpdateCashflowMutation,
  useUpdateTodoMutation,
  useUpdateEmployeeMutation,
  useUpdateGalleryItemMutation,
  useUpdateOrderMutation,
  useUpdatePaymentMutation,
  useUpdateStockItemMutation,
  useUpdateStockMovementMutation,
  useUpdateTaskMutation,
} = api;
